/**
 * Flusso di login "standard Cognito" basato su AWS SDK v3
 * (`@aws-sdk/client-cognito-identity-provider`), attivo solo quando
 * VITE_LOGIN_STANDARD_COGNITO === "true".
 *
 * Rispetto al flusso custom (amazon-cognito-identity-js / CUSTOM_AUTH), qui
 * gestiamo esplicitamente le challenge MFA native di Cognito (EMAIL_OTP,
 * SOFTWARE_TOKEN_MFA, SELECT_MFA_TYPE) e NEW_PASSWORD_REQUIRED.
 *
 * Gestiamo anche MFA_SETUP (enrollment TOTP durante login) via Session.
 * NON gestiamo il device tracking (ConfirmDevice / NewDeviceMetadata).
 *
 * NOTA SICUREZZA: USER_PASSWORD_AUTH invia la password su TLS senza SRP.
 * NOTA: il client dell'app non ha secret (generateSecret=false), quindi non
 * viene calcolato alcun SECRET_HASH lato frontend.
 */
import {
  AssociateSoftwareTokenCommand,
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  VerifySoftwareTokenCommand,
  type AuthenticationResultType,
  type RespondToAuthChallengeCommandInput
} from "@aws-sdk/client-cognito-identity-provider";
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession
} from "amazon-cognito-identity-js";
import { getMfaLoginPreference } from "./mfaLoginPreference";
import { COGNITO_V3_CHALLENGE, cognitoConfig, MFA_FACTOR } from "../consts";
import { parseJwt } from "../utils/general";
import type { MfaFactor } from "../utils/mfa";

const client = new CognitoIdentityProviderClient({ region: cognitoConfig.Region });

// Riusiamo lo stesso userPool del flusso custom: così l'utente/sessione
// ricostruiti condividono ClientId/UserPoolId e il refresh (via
// amazon-cognito-identity-js) continua a funzionare senza modifiche allo store.
const userPool = new CognitoUserPool({
  ClientId: cognitoConfig.ClientId,
  UserPoolId: cognitoConfig.UserPoolId
});

/** Esito normalizzato di un passo di autenticazione. */
export type V3AuthResult =
  | {
      type: "TOKENS"
      session: CognitoUserSession
      user: CognitoUser
    }
  | {
      type: "CHALLENGE"
      challengeName: string
      session: string
      username: string
      parameters: Record<string, string>
      availableMfaFactors?: MfaFactor[]
    };

/**
 * Ricostruisce CognitoUserSession + CognitoUser dai token "grezzi" dell'SDK v3
 * e li scrive nella cache localStorage tramite `setSignInUserSession`
 * (stesso meccanismo di `recoverAuthStore`), così il resto dell'app
 * (refresh, persist, signOut, cookie admin) resta invariato.
 */
function buildSessionFromTokens(
  authResult: AuthenticationResultType
): { session: CognitoUserSession, user: CognitoUser } {
  const accessTokenJwt = authResult.AccessToken;
  const idTokenJwt = authResult.IdToken;
  const refreshTokenStr = authResult.RefreshToken;

  if (!accessTokenJwt || !idTokenJwt || !refreshTokenStr) {
    throw new Error("authCognitoV3: AuthenticationResult senza token completi");
  }

  const session = new CognitoUserSession({
    AccessToken: new CognitoAccessToken({ AccessToken: accessTokenJwt }),
    IdToken: new CognitoIdToken({ IdToken: idTokenJwt }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshTokenStr })
  });

  // Username canonico = claim "cognito:username" dell'id token: garantisce
  // coerenza con LastAuthUser usato dal refresh della libreria vecchia.
  const username = parseJwt(idTokenJwt)["cognito:username"] as string;

  const user = new CognitoUser({ Pool: userPool, Username: username });
  // Mantiene coerenza con il resto dell'app (recoverAuthStore usa CUSTOM_AUTH).
  user.setAuthenticationFlowType("CUSTOM_AUTH");
  user.setSignInUserSession(session); // popola la cache localStorage

  return { session, user };
}

/** Normalizza la risposta di InitiateAuth / RespondToAuthChallenge. */
function normalizeAuthOutput(
  out: {
    AuthenticationResult?: AuthenticationResultType
    ChallengeName?: string
    Session?: string
    ChallengeParameters?: Record<string, string>
  },
  fallbackUsername: string
): V3AuthResult {
  if (out.AuthenticationResult) {
    return { type: "TOKENS", ...buildSessionFromTokens(out.AuthenticationResult) };
  }

  if (!out.ChallengeName || !out.Session) {
    throw new Error("authCognitoV3: risposta senza token né challenge valida");
  }

  return {
    challengeName: out.ChallengeName,
    parameters: out.ChallengeParameters ?? {},
    session: out.Session,
    type: "CHALLENGE",
    // In alcune challenge (es. EMAIL_OTP) Cognito rimanda USER_ID_FOR_SRP;
    // altrimenti usiamo lo username con cui abbiamo avviato il login.
    username: out.ChallengeParameters?.USER_ID_FOR_SRP ?? fallbackUsername
  };
}

/**
 * Avvia il login con username + password (USER_PASSWORD_AUTH).
 * In caso di SELECT_MFA_TYPE usa la scelta esplicita, oppure il preferito salvato sul BE.
 * Ritorna i token oppure la challenge da mostrare all'utente.
 */
export async function loginV3(
  username: string,
  password: string,
  corporateId: number,
  selectedFactor?: MfaFactor
): Promise<V3AuthResult> {
  const out = await client.send(new InitiateAuthCommand({
    AuthFlow: "USER_PASSWORD_AUTH",
    AuthParameters: { PASSWORD: password, USERNAME: username },
    ClientId: cognitoConfig.ClientId
  }));
  const result = normalizeAuthOutput(out, username);
  if (result.type !== "CHALLENGE" || result.challengeName !== COGNITO_V3_CHALLENGE.SELECT_MFA_TYPE) {
    return result;
  }

  // Lo username di avvio è l'external_id recuperato dal BE; Cognito può restituire un nome canonico diverso.
  const preferredFactor = selectedFactor ?? await getMfaLoginPreference(username, corporateId);
  const availableFactors = Object.values(MFA_FACTOR);
  if (!preferredFactor || !availableFactors.includes(preferredFactor)) {
    return { ...result, availableMfaFactors: availableFactors };
  }

  // Riusa la Session e lo username dell'ultima risposta Cognito, come nella scelta manuale.
  // Gli errori Cognito restano gestiti dal normale flusso di login nello store.
  const nextResult = await respondToChallengeV3({
    answer: preferredFactor,
    challengeName: result.challengeName,
    session: result.session,
    username: result.username
  });
  // La challenge OTP non ripete necessariamente i metodi disponibili nella selezione.
  return nextResult.type === "CHALLENGE"
    ? { ...nextResult, availableMfaFactors: availableFactors }
    : nextResult;
}

/**
 * Costruisce le ChallengeResponses corrette in base al tipo di challenge.
 * `answer` è il valore inserito dall'utente (codice OTP, nuova password, o
 * il fattore scelto per SELECT_MFA_TYPE).
 */
function buildChallengeResponses(
  challengeName: string,
  username: string,
  answer: string
): Record<string, string> {
  const base = { USERNAME: username };
  switch (challengeName) {
  case COGNITO_V3_CHALLENGE.EMAIL_OTP:
    return { ...base, EMAIL_OTP_CODE: answer };
  case COGNITO_V3_CHALLENGE.SOFTWARE_TOKEN_MFA:
    return { ...base, SOFTWARE_TOKEN_MFA_CODE: answer };
  case COGNITO_V3_CHALLENGE.SELECT_MFA_TYPE:
    return { ...base, ANSWER: answer };
  case COGNITO_V3_CHALLENGE.NEW_PASSWORD_REQUIRED:
    return { ...base, NEW_PASSWORD: answer };
  default:
    throw new Error(`authCognitoV3: challenge non gestita: ${challengeName}`);
  }
}

/**
 * Risponde a una challenge e ritorna l'esito successivo (token o nuova challenge).
 * La `Session` da passare è SEMPRE quella dell'ultimo passo.
 */
export async function respondToChallengeV3({
  challengeName,
  session,
  username,
  answer
}: {
  challengeName: string
  session: string
  username: string
  answer: string
}): Promise<V3AuthResult> {
  const input: RespondToAuthChallengeCommandInput = {
    ChallengeName: challengeName as RespondToAuthChallengeCommandInput["ChallengeName"],
    ChallengeResponses: buildChallengeResponses(challengeName, username, answer),
    ClientId: cognitoConfig.ClientId,
    Session: session
  };
  const out = await client.send(new RespondToAuthChallengeCommand(input));
  return normalizeAuthOutput(out, username);
}

/**
 * Avvia l'enrollment TOTP durante MFA_SETUP usando la Session della challenge.
 * Restituisce il secret da mostrare in QR e la Session aggiornata.
 */
export async function startTotpSetupV3({
  session
}: {
  session: string
}): Promise<{ secretCode: string, session: string }> {
  const out = await client.send(new AssociateSoftwareTokenCommand({ Session: session }));
  if (!out.SecretCode || !out.Session) {
    throw new Error("authCognitoV3: AssociateSoftwareToken senza SecretCode/Session");
  }

  return {
    secretCode: out.SecretCode,
    session: out.Session
  };
}

/**
 * Verifica il codice TOTP durante MFA_SETUP e completa la challenge MFA_SETUP.
 */
export async function verifyTotpSetupV3({
  code,
  session,
  username
}: {
  code: string
  session: string
  username: string
}): Promise<V3AuthResult> {
  const verification = await client.send(new VerifySoftwareTokenCommand({
    FriendlyDeviceName: "Authenticator",
    Session: session,
    UserCode: code
  }));

  if (verification.Status !== "SUCCESS") {
    throw new Error("authCognitoV3: VerifySoftwareToken fallita");
  }

  const out = await client.send(new RespondToAuthChallengeCommand({
    ChallengeName: "MFA_SETUP",
    ChallengeResponses: { USERNAME: username },
    ClientId: cognitoConfig.ClientId,
    Session: verification.Session ?? session
  }));

  return normalizeAuthOutput(out, username);
}

/**
 * Avvia l'enrollment TOTP per un utente gia autenticato (es. pagina profilo).
 * In questo caso Cognito usa l'AccessToken invece della Session di challenge.
 */
export async function startTotpSetupAuthenticatedV3({
  accessToken
}: {
  accessToken: string
}): Promise<{ secretCode: string }> {
  const out = await client.send(new AssociateSoftwareTokenCommand({ AccessToken: accessToken }));
  if (!out.SecretCode) {
    throw new Error("authCognitoV3: AssociateSoftwareToken senza SecretCode");
  }

  return { secretCode: out.SecretCode };
}

/**
 * Verifica il codice TOTP per utente gia autenticato (es. pagina profilo).
 */
export async function verifyTotpSetupAuthenticatedV3({
  accessToken,
  code
}: {
  accessToken: string
  code: string
}): Promise<void> {
  const verification = await client.send(new VerifySoftwareTokenCommand({
    AccessToken: accessToken,
    FriendlyDeviceName: "Authenticator",
    UserCode: code
  }));

  if (verification.Status !== "SUCCESS") {
    throw new Error("authCognitoV3: VerifySoftwareToken fallita");
  }
}
